'use client';

import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from 'react';
import { formatPhoneForDisplay, normalizePhoneNumberPaste } from '../lib/phoneUtils';

export type PhoneNumberInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'onPaste' | 'type' | 'defaultValue'
> & {
  /** Valor em dígitos (pode conter * e # para DTMF). */
  value: string;
  /** Recebe os dígitos digitados/colados, nunca a máscara. */
  onChange: (digits: string) => void;
  /** Chamado ao pressionar Enter. */
  onSubmit?: () => void;
};

const DIAL_CHAR = /[0-9*#]/;

/** Extrai só o que o discador aceita do texto exibido no input. */
export function sanitizeTypedPhone(display: string): string {
  return (display ?? '').replace(/[^0-9*#]/g, '');
}

function countDialChars(text: string): number {
  let count = 0;
  for (const char of text) {
    if (DIAL_CHAR.test(char)) count++;
  }
  return count;
}

/** Posição do caret na string formatada logo após `digitCount` dígitos. */
export function caretPositionAfterDigits(display: string, digitCount: number): number {
  if (digitCount <= 0) {
    // antes do primeiro dígito (pula "(")
    const first = display.search(DIAL_CHAR);
    return first < 0 ? display.length : first;
  }
  let seen = 0;
  for (let i = 0; i < display.length; i++) {
    if (DIAL_CHAR.test(display[i])) {
      seen++;
      if (seen === digitCount) {
        return i + 1;
      }
    }
  }
  return display.length;
}

/**
 * Input de telefone controlado com máscara brasileira.
 *
 * - O visor mostra o número formatado ("(51) 3248-8509"), mas o estado é só de
 *   dígitos, extraídos de volta do que está na tela a cada edição. Por isso a
 *   máscara nunca "cria" dígitos e apagar um dígito (inclusive o 9) funciona.
 * - O caret é mantido no mesmo ponto (contado em dígitos) ao editar no meio;
 *   Backspace/Delete sobre um caractere da máscara apaga o dígito vizinho.
 * - Colar normaliza o texto (remove +55/0/operadora, acrescenta o 9 só em
 *   celular antigo) e substitui o valor inteiro.
 */
export const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(function PhoneNumberInput(
  { value, onChange, onSubmit, onKeyDown, inputMode = 'tel', autoComplete = 'off', ...rest },
  ref
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingCaretDigitsRef = useRef<number | null>(null);
  const display = formatPhoneForDisplay(value);
  const lastDisplayRef = useRef(display);

  const setRefs = useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref]
  );

  // Reposiciona o caret depois que o React reescreveu o valor formatado.
  useLayoutEffect(() => {
    lastDisplayRef.current = display;
    const element = inputRef.current;
    const digitCount = pendingCaretDigitsRef.current;
    if (!element || digitCount === null) {
      return;
    }
    pendingCaretDigitsRef.current = null;
    if (typeof document !== 'undefined' && document.activeElement !== element) {
      return;
    }
    const position = caretPositionAfterDigits(display, digitCount);
    try {
      element.setSelectionRange(position, position);
    } catch {
      // alguns tipos de input não suportam seleção
    }
  }, [display]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const element = event.target;
      const domValue = element.value;
      const caret = element.selectionStart ?? domValue.length;
      const previousDisplay = lastDisplayRef.current;
      const previousDigits = sanitizeTypedPhone(previousDisplay);
      const inputType = (event.nativeEvent as InputEvent | undefined)?.inputType ?? '';

      let digitsBeforeCaret = countDialChars(domValue.slice(0, caret));
      let next = sanitizeTypedPhone(domValue);

      // Backspace/Delete sobre "(", ")", " " ou "-": os dígitos não mudaram,
      // então apaga o dígito vizinho ao caret.
      if (next === previousDigits && domValue.length < previousDisplay.length) {
        if (inputType === 'deleteContentForward') {
          if (digitsBeforeCaret < next.length) {
            next = next.slice(0, digitsBeforeCaret) + next.slice(digitsBeforeCaret + 1);
          }
        } else if (digitsBeforeCaret > 0) {
          next = next.slice(0, digitsBeforeCaret - 1) + next.slice(digitsBeforeCaret);
          digitsBeforeCaret -= 1;
        }
      }

      if (next === previousDigits) {
        // Nada mudou (ex.: letra digitada): restaura a máscara e o caret sem re-render.
        element.value = previousDisplay;
        const position = caretPositionAfterDigits(previousDisplay, digitsBeforeCaret);
        try {
          element.setSelectionRange(position, position);
        } catch {
          // ignore
        }
        return;
      }

      pendingCaretDigitsRef.current = digitsBeforeCaret;
      onChange(next);
    },
    [onChange]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData?.getData('text') ?? '';
      if (!text) {
        return;
      }
      event.preventDefault();
      const normalized = normalizePhoneNumberPaste(text);
      if (normalized) {
        pendingCaretDigitsRef.current = normalized.length;
        onChange(normalized);
      }
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) {
        return;
      }
      if (event.key === 'Enter' && onSubmit) {
        event.preventDefault();
        onSubmit();
      }
    },
    [onKeyDown, onSubmit]
  );

  return (
    <input
      ref={setRefs}
      type="tel"
      inputMode={inputMode}
      autoComplete={autoComplete}
      value={display}
      onChange={handleChange}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      {...rest}
    />
  );
});
